import { type FC, type RefObject, type ReactNode, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';

import dibsCss from 'dibs-css';
import { FormattedPrice } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { showPricingGuidance as showPricingGuidanceHelper } from 'dibs-pricing-guidance/exports/showPricingGuidance.ts';
import { getPriceAmountByType } from 'dibs-pricing-guidance/exports/pricingGuidanceHelpers.ts';
import { useGetEstimatedValueRange } from 'dibs-pricing-guidance/exports/useGetEstimatedValueRange.ts';
import { trackPricingGuidanceRangeDisplay } from 'dibs-pricing-guidance/exports/pricingGuidanceTracking.ts';

import { trackEstimateSimilarSoldRangeClick } from './estimateSimilarSoldTracking.ts';

import { type PdpSharedEstimateSimilarSoldRange_item$key } from './__generated__/PdpSharedEstimateSimilarSoldRange_item.graphql.ts';

import styles from './PdpSharedEstimateSimilarSoldRange.module.css';

type PdpSharedEstimateSimilarSoldRangeProps = {
    item: PdpSharedEstimateSimilarSoldRange_item$key;
    currency: string;
    estimatePricingRef?: RefObject<HTMLElement | null>;
};

const PdpSharedEstimateSimilarSoldRange: FC<PdpSharedEstimateSimilarSoldRangeProps> = ({
    item: itemRef,
    currency,
    estimatePricingRef,
}) => {
    const item = useFragment(
        graphql`
            fragment PdpSharedEstimateSimilarSoldRange_item on Item {
                ...useGetEstimatedValueRange_item
                ...pricingGuidanceHelpers_item
                ...showPricingGuidance_item
                ...pricingGuidanceTracking_item
            }
        `,
        itemRef
    );

    const showPricingGuidance = showPricingGuidanceHelper({ item, currency });
    const { rangeMin, rangeMax } = useGetEstimatedValueRange({ item, currency });
    const itemPrice = getPriceAmountByType({ item, currency, type: 'ITEM_PRICE' });

    useEffect(() => {
        if (showPricingGuidance) {
            trackPricingGuidanceRangeDisplay({ item });
        }
    }, [showPricingGuidance, item]);

    if (!showPricingGuidance) {
        return null;
    }

    const getValueBadge = (): ReactNode => {
        let valueBadge;

        if (itemPrice <= rangeMax) {
            const isBelowEstimate = itemPrice < rangeMin;

            valueBadge = (
                <Badge
                    type="pill"
                    size="small-heavy"
                    backgroundColor={isBelowEstimate ? 'green-50' : 'sand-50'}
                >
                    {isBelowEstimate ? (
                        <FormattedMessage
                            id="PdpSharedEstimateSimilarSoldRange.excelentValue"
                            defaultMessage="Excellent Value"
                        />
                    ) : (
                        <FormattedMessage
                            id="PdpSharedEstimateSimilarSoldRange.competitivePrice"
                            defaultMessage="Competitive Price"
                        />
                    )}
                </Badge>
            );
        }

        return valueBadge;
    };

    return (
        <div className={classnames(dibsCss.flex, dibsCss.justifyBetween, dibsCss.flexWrap)}>
            <div className={classnames(dibsCss.sassyFontBodySmall, dibsCss.textGray700)}>
                <FormattedMessage
                    id="PdpSharedEstimateSimilarSoldRange.estimate"
                    defaultMessage="1stDibs Estimate:"
                />
                <span className={dibsCss.mlXxsmall}>
                    <Link
                        className={styles.rangeLink}
                        onClick={() => {
                            const estimatePricingRefEl = estimatePricingRef?.current;

                            if (estimatePricingRefEl) {
                                scrollIntoView(
                                    estimatePricingRefEl,
                                    { time: 500, align: { top: 0.1, topOffset: 0 } },
                                    estimatePricingRefEl.focus()
                                );
                            }

                            trackEstimateSimilarSoldRangeClick();
                        }}
                    >
                        <FormattedMessage
                            id="PdpSharedEstimateSimilarSoldRange.estimateRange"
                            defaultMessage="{rangeMin} - {rangeMax}"
                            values={{
                                rangeMin: (
                                    <FormattedPrice
                                        key="rangeMin"
                                        value={rangeMin}
                                        currency={currency}
                                    />
                                ),
                                rangeMax: (
                                    <FormattedPrice
                                        key="rangeMax"
                                        value={rangeMax}
                                        currency={currency}
                                    />
                                ),
                            }}
                        />
                    </Link>
                </span>
            </div>
            {getValueBadge()}
        </div>
    );
};

export default PdpSharedEstimateSimilarSoldRange;
